import { LogChannel, LogEntry, LogSearchResponse, LogUser } from "../../../core/types.js";
import { SharedLogSearchRequestQueue } from "../../../utils/logSearchRequests.js";

export type MessageContextResult = {
    logs: LogEntry[];
    members: LogUser[];
    channels: LogChannel[];
    anchorLog: LogEntry | null;
    selectedLog: LogEntry | null;
};

type LogSearchQueue = {
    fetchJson<TPayload extends Record<string, unknown>>(url: string, options?: RequestInit): Promise<TPayload>;
};

type MessageContextResponse = LogSearchResponse & {
    result: LogEntry[];
};

export type MessageContextMode = "channel" | "executor" | "target";

type MessageContextFilter = {
    mode: MessageContextMode;
    channel: string | null | undefined;
    executor?: string | null;
    target?: string | null;
};

const MessageContextLogTypes = [ "message_create", "message_edit", "message_delete" ];
const PageSize = 25;
const ReactionLookupWindowMs = 6 * 60 * 60_000;

function normalizeLogWindow({
    after,
    before,
}: {
    after?: number;
    before?: number;
}): { after?: number; before?: number } {
    const now = Date.now();
    const normalizedBefore = typeof before === "number"
        ? Math.min(Math.max(1, before), now)
        : undefined;
    const maxAfter = typeof normalizedBefore === "number"
        ? Math.max(0, normalizedBefore - 1)
        : Math.max(0, now - 1);
    const normalizedAfter = typeof after === "number"
        ? Math.min(Math.max(0, after), maxAfter)
        : undefined;

    return {
        after  : normalizedAfter,
        before : normalizedBefore,
    };
}

function createJsonPostOptions(body: Record<string, unknown>): RequestInit {
    return {
        headers: {
            "content-type": "application/json",
        },
        body    : JSON.stringify(body),
        method  : "POST",
    };
}

function messageSnowflakeToTimestamp(messageId: unknown): number | null {
    if (typeof messageId !== "string" || !/^\d+$/.test(messageId))
        return null;

    return Number(BigInt(messageId) >> 22n) + 1420070400000;
}

function mergeContextResults(...results: MessageContextResult[]): MessageContextResult {
    const logs = new Map<string, LogEntry>();
    const members = new Map<string, LogUser>();
    const channels = new Map<string, LogChannel>();
    let anchorLog: LogEntry | null = null;
    let selectedLog: LogEntry | null = results[0]?.selectedLog ?? null;

    for (const result of results) {
        anchorLog ??= result.anchorLog;
        selectedLog ??= result.selectedLog;

        for (const log of result.logs)
            logs.set(`${log.type}:${log.createdAt}:${log.id ?? ""}`, log);

        for (const member of result.members)
            if (member)
                members.set(member.id, member);

        for (const channel of result.channels)
            if (channel)
                channels.set(channel.id, channel);
    }

    return {
        logs        : [ ...logs.values() ].sort((left, right) => left.createdAt - right.createdAt),
        members     : [ ...members.values() ],
        channels    : [ ...channels.values() ],
        anchorLog,
        selectedLog,
    };
}

function createMessageContextPayload(
    mode: MessageContextMode,
    channel: string | null | undefined,
    executor?: string | null,
    target?: string | null,
): Record<string, unknown> {
    const payload: Record<string, unknown> = {
        types    : MessageContextLogTypes,
        ...(channel ? { channels: [ channel ] } : {}),
    };

    if (mode === "executor" && executor)
        payload.executors = [ executor ];

    if (mode === "target" && target)
        payload.targets = [ target ];

    return payload;
}

function isMode(value: MessageContextMode | LogSearchQueue): value is MessageContextMode {
    return typeof value === "string"
        && (value === "channel" || value === "executor" || value === "target");
}

async function fetchMessageLogPage({
    after,
    before,
    channel,
    mode = "channel",
    executor,
    target,
    orderBy,
    queue,
    limit = PageSize,
}: {
    after?: number;
    before?: number;
    channel: string | null | undefined;
    mode?: MessageContextMode;
    executor?: string | null;
    target?: string | null;
    orderBy: "ASC" | "DESC";
    queue: LogSearchQueue;
    limit?: number;
}): Promise<MessageContextResult> {
    const window = normalizeLogWindow({ after, before });
    const params = new URLSearchParams({
        limit   : String(limit),
        orderBy,
    });

    if (typeof window.before === "number")
        params.set("before", String(window.before));

    if (typeof window.after === "number")
        params.set("after", String(window.after));

    const payload = createMessageContextPayload(
        mode,
        channel,
        mode === "executor" ? executor : undefined,
        mode === "target" ? target : undefined,
    );

    const response = await queue.fetchJson<MessageContextResponse>(`/api/logs?${params.toString()}`, createJsonPostOptions({
        ...payload,
    }));

    return {
        logs        : response.result ?? [],
        members     : response.members ?? [],
        channels    : response.channels ?? [],
        anchorLog   : null,
        selectedLog : response.result?.[0] ?? null,
    };
}

function asContextFilter(filter: MessageContextFilter | string | null | undefined): MessageContextFilter {
    if (typeof filter === "string" || filter == null)
        return {
            mode    : "channel",
            channel : filter,
        };

    return filter;
}

async function fetchMessageCreateByMessageId({
    channel,
    messageId,
    timestamp,
    queue,
}: {
    channel: string | null | undefined;
    messageId: string;
    timestamp: number;
    queue: LogSearchQueue;
}): Promise<MessageContextResult> {
    const window = normalizeLogWindow({
        after  : timestamp - ReactionLookupWindowMs,
        before : timestamp + ReactionLookupWindowMs,
    });
    const params = new URLSearchParams({
        after  : String(window.after ?? 0),
        before : String(window.before ?? Date.now()),
        limit  : "1",
    });

    const response = await queue.fetchJson<MessageContextResponse>(
        `/api/logs?${params.toString()}`,
        createJsonPostOptions({
            mode    : "expression",
            query   : `type == "message_create" and channel == "${channel}" and extra.messageId == "${messageId}"`,
        }),
    );

    return {
        logs        : response.result ?? [],
        members     : response.members ?? [],
        channels    : response.channels ?? [],
        anchorLog   : response.result?.[0] ?? null,
        selectedLog : response.result?.[0] ?? null,
    };
}

export async function fetchPreviousMessageContextPage(
    logs: readonly LogEntry[],
    filter: MessageContextFilter | string | null | undefined,
    queue: LogSearchQueue = SharedLogSearchRequestQueue,
): Promise<MessageContextResult> {
    const oldest = logs.reduce<LogEntry | null>((current, log) => {
        return !current || log.createdAt < current.createdAt ? log : current;
    }, null);

    if (!oldest)
        return { logs: [], members: [], channels: [], anchorLog: null, selectedLog: null };

    return fetchMessageLogPage({
        before  : oldest.createdAt,
        ...(asContextFilter(filter)),
        orderBy : "DESC",
        queue,
    });
}

export async function fetchNextMessageContextPage(
    logs: readonly LogEntry[],
    filter: MessageContextFilter | string | null | undefined,
    queue: LogSearchQueue = SharedLogSearchRequestQueue,
): Promise<MessageContextResult> {
    const newest = logs.reduce<LogEntry | null>((current, log) => {
        return !current || log.createdAt > current.createdAt ? log : current;
    }, null);

    if (!newest)
        return { logs: [], members: [], channels: [], anchorLog: null, selectedLog: null };

    return fetchMessageLogPage({
        after   : newest.createdAt,
        ...(asContextFilter(filter)),
        orderBy : "ASC",
        queue,
    });
}

export async function loadMessageContext(
    log: LogEntry,
    modeOrQueue: MessageContextMode | LogSearchQueue = "channel",
    executor?: string | null,
    target?: string | null,
    queue: LogSearchQueue = SharedLogSearchRequestQueue,
): Promise<MessageContextResult> {
    const hasMode = isMode(modeOrQueue);
    const mode: MessageContextMode = hasMode ? modeOrQueue : "channel";
    const requestQueue: LogSearchQueue = hasMode
        ? queue
        : (modeOrQueue ?? SharedLogSearchRequestQueue);

    async function fetchAround(anchorLog: LogEntry, selectedLog: LogEntry): Promise<MessageContextResult> {
        const previous = await fetchMessageLogPage({
            before  : anchorLog.createdAt,
            channel : anchorLog.channel,
            mode,
            executor,
            target,
            orderBy : "DESC",
            queue   : requestQueue,
        });
        const anchor = await fetchMessageLogPage({
            before  : anchorLog.createdAt + 1,
            channel : anchorLog.channel,
            mode,
            executor,
            target,
            orderBy : "DESC",
            queue   : requestQueue,
            limit    : 1,
        });
        const next = await fetchMessageLogPage({
            after   : anchorLog.createdAt,
            channel : anchorLog.channel,
            mode,
            executor,
            target,
            orderBy : "ASC",
            queue   : requestQueue,
        });

        return {
            ...mergeContextResults(previous, anchor, next),
            anchorLog,
            selectedLog,
        };
    }

    if (log.type.startsWith("message_reaction")) {
        const createdAt = messageSnowflakeToTimestamp(log.extra?.messageId);

        if (!createdAt)
            return { logs: [], members: [], channels: [], anchorLog: null, selectedLog: log };

        const relatedResponse = await fetchMessageCreateByMessageId({
            channel     : log.channel,
            messageId   : String(log.extra?.messageId),
            timestamp   : createdAt,
            queue       : requestQueue,
        });
        const relatedMessageLog = relatedResponse.logs[0];

        if (!relatedMessageLog)
            return {
                logs        : [],
                members     : relatedResponse.members,
                channels    : relatedResponse.channels,
                anchorLog   : null,
                selectedLog : log,
            };

        const context = await fetchAround(relatedMessageLog, log);

        return {
            logs        : context.logs,
            members     : [ ...relatedResponse.members, ...context.members ],
            channels    : [ ...relatedResponse.channels, ...context.channels ],
            anchorLog   : relatedMessageLog,
            selectedLog : log,
        };
    }

    if (log.type.startsWith("message_"))
        return await fetchAround(log, log);

    return {
        logs        : [],
        members     : [],
        channels    : [],
        anchorLog   : null,
        selectedLog : log,
    };
}
